import { Bold, Italic, Link2, List, Undo2 } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeHtml } from '../../utils/sanitizeHtml.js';

/**
 * A deliberately small editor. It wraps the current Selection rather than
 * document.execCommand, which is deprecated and behaves differently in every
 * engine, and everything it emits is sanitized before it leaves the component.
 */
const wrapSelection = (root, build) => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return false;
  if (range.collapsed) return false;

  const wrapper = build();
  try {
    wrapper.appendChild(range.extractContents());
    range.insertNode(wrapper);
    selection.removeAllRanges();

    const next = document.createRange();
    next.selectNodeContents(wrapper);
    selection.addRange(next);
    return true;
  } catch {
    return false;
  }
};

const toList = (root) => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return false;

  const text = range.toString().trim();
  const lines = (text ? text.split(/\n+/) : ['']).filter((line, index, list) => line || list.length === 1);

  const list = document.createElement('ul');
  lines.forEach((line) => {
    const item = document.createElement('li');
    item.textContent = line;
    list.appendChild(item);
  });

  range.deleteContents();
  range.insertNode(list);
  selection.removeAllRanges();
  return true;
};

export const RichTextEditor = ({ value, onChange, label, helper }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const id = useId();
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const savedRange = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const emit = () => onChange(sanitizeHtml(ref.current?.innerHTML || ''));

  const run = (action) => {
    ref.current?.focus();
    action(ref.current);
    emit();
  };

  const openLink = () => {
    const selection = window.getSelection();
    if (selection?.rangeCount && !selection.getRangeAt(0).collapsed) {
      savedRange.current = selection.getRangeAt(0).cloneRange();
      setLinkUrl('');
      setLinkOpen(true);
    }
  };

  const applyLink = (event) => {
    event.preventDefault();
    const url = linkUrl.trim();
    setLinkOpen(false);
    if (!url || !/^(https?:|mailto:|tel:)/i.test(url)) return;

    const selection = window.getSelection();
    selection?.removeAllRanges();
    if (savedRange.current) selection?.addRange(savedRange.current);

    run((root) =>
      wrapSelection(root, () => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.rel = 'noopener noreferrer';
        return anchor;
      }),
    );
  };

  const tools = [
    [Bold, t('admin.editorBold'), () => run((root) => wrapSelection(root, () => document.createElement('strong')))],
    [Italic, t('admin.editorItalic'), () => run((root) => wrapSelection(root, () => document.createElement('em')))],
    [List, t('admin.editorList'), () => run(toList)],
    [Link2, t('admin.editorLink'), openLink],
  ];

  return (
    <div className="grid gap-2 text-sm font-bold">
      <label htmlFor={id}>{label}</label>
      {helper ? <p className="text-xs font-semibold text-beam-graphite/60">{helper}</p> : null}

      <div className="rounded-lg border border-beam-blue/10 bg-white">
        <div className="flex flex-wrap items-center gap-1 border-b border-beam-blue/10 p-2">
          {tools.map(([Icon, title, onClick]) => (
            <button
              key={title}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={onClick}
              className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-lg bg-beam-light text-beam-ink hover:bg-white"
              title={title}
              aria-label={title}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
            </button>
          ))}
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              if (ref.current) ref.current.innerHTML = '';
              emit();
            }}
            className="focus-ring ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg px-2 text-xs font-extrabold text-beam-graphite hover:bg-beam-light"
            title={t('admin.editorClear')}
          >
            <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t('admin.editorClear')}
          </button>
        </div>

        {linkOpen ? (
          <div className="flex flex-wrap items-end gap-2 border-b border-beam-blue/10 bg-beam-light/50 p-2">
            <label className="grid flex-1 gap-1 text-[11px] font-extrabold uppercase tracking-wide text-beam-graphite/60">
              {t('admin.editorLinkUrl')}
              <input
                value={linkUrl}
                onChange={(event) => setLinkUrl(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') applyLink(event);
                  if (event.key === 'Escape') setLinkOpen(false);
                }}
                placeholder="https://"
                autoFocus
                className="focus-ring rounded-lg border border-beam-blue/10 px-3 py-2 text-sm font-semibold normal-case tracking-normal outline-none"
              />
            </label>
            <button
              type="button"
              onClick={applyLink}
              className="focus-ring h-9 rounded-lg bg-beam-blue px-3 text-xs font-extrabold text-white"
            >
              {t('admin.editorApply')}
            </button>
            <button
              type="button"
              onClick={() => setLinkOpen(false)}
              className="focus-ring h-9 rounded-lg px-3 text-xs font-extrabold text-beam-graphite hover:bg-white"
            >
              {t('common.cancel')}
            </button>
          </div>
        ) : null}

        <div
          id={id}
          ref={ref}
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          tabIndex={0}
          onInput={emit}
          onBlur={emit}
          onPaste={(event) => {
            // Pasting from Word or a web page otherwise drags a whole stylesheet in.
            event.preventDefault();
            const text = event.clipboardData.getData('text/plain');
            const selection = window.getSelection();
            if (!selection?.rangeCount) return;
            selection.deleteFromDocument();
            selection.getRangeAt(0).insertNode(document.createTextNode(text));
            selection.collapseToEnd();
            emit();
          }}
          className="focus-ring min-h-36 rounded-b-lg px-4 py-3 font-medium leading-7 outline-none"
          suppressContentEditableWarning
        />
      </div>
    </div>
  );
};
